import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Lock } from "lucide-react";
import { IMAGES } from "@/lib/site";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/academics", label: "Academics" },
  { to: "/gallery", label: "Gallery" },
  { to: "/notices", label: "Notices" },
  { to: "/principal", label: "Principal" },
  { to: "/chairman", label: "Chairman" },
  { to: "/reviews", label: "Reviews" },
  { to: "/contact", label: "Contact" },
  { to: "/admin/login", label: "Admin", isAdmin: true },
];

function LogoMark({ size = 12 }) {
  // size is tailwind w-/h- value
  return (
    <div className={`w-${size} h-${size} rounded-full bg-royal-dark ring-2 ring-gold/60 shadow-lg flex items-center justify-center overflow-hidden`}>
      <img src={IMAGES.logo} alt="Royal English Medium High School logo" className="w-full h-full object-contain p-1" />
    </div>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const renderNavItem = (l, isMobile = false) => {
    const testId = `${isMobile ? "mobile-" : ""}nav-${l.label.toLowerCase()}`;
    return (
      <NavLink
        key={l.to}
        to={l.to}
        data-testid={testId}
        className={({ isActive }) => {
          if (l.isAdmin) {
            return isMobile
              ? `py-2 font-semibold inline-flex items-center gap-2 ${isActive ? "text-gold" : "text-royal"}`
              : `inline-flex items-center gap-1.5 text-sm font-semibold tracking-wide border border-royal/30 px-3 py-1.5 rounded-full transition-all ${
                  isActive ? "bg-royal text-white border-royal" : "text-royal hover:bg-royal hover:text-white hover:border-royal"
                }`;
          }
          return isMobile
            ? `py-2 font-semibold ${isActive ? "text-gold" : "text-royal"}`
            : `text-sm font-semibold tracking-wide transition-colors ${isActive ? "text-gold" : "text-royal hover:text-gold"}`;
        }}
      >
        {l.isAdmin && <Lock size={isMobile ? 14 : 12} />}
        {l.label}
      </NavLink>
    );
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled ? "bg-white nav-shadow border-b border-gold/40" : "bg-white/95 backdrop-blur-md"
      }`}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between px-5 md:px-8 py-3">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-3 group">
          <LogoMark size={12} />
          <div className="leading-tight">
            <h1 className="font-serif-display text-xl md:text-2xl font-bold text-royal">ROYAL ENGLISH</h1>
            <p className="text-[10px] md:text-xs text-gold font-semibold tracking-[0.2em]">MEDIUM HIGH SCHOOL</p>
          </div>
        </Link>

        <nav className="hidden xl:flex items-center gap-6">
          {links.map((l) => renderNavItem(l, false))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/admission"
            data-testid="apply-now-btn"
            className="hidden md:inline-flex bg-gold hover:bg-royal text-royal hover:text-white px-6 py-2.5 rounded-full font-bold text-sm shadow-md transition-all"
          >
            Apply Now
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className="xl:hidden p-2 text-royal"
            aria-label="Toggle menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="xl:hidden bg-white border-t border-gold/30">
          <div className="px-5 py-4 flex flex-col gap-3">
            {links.map((l) => renderNavItem(l, true))}
            <Link
              to="/admission"
              data-testid="mobile-apply-now-btn"
              className="bg-gold text-royal px-5 py-2.5 rounded-full font-bold text-center mt-2"
            >
              Apply Now
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
